import { useEffect, useSyncExternalStore } from "react"

const openModals = new Set<symbol>()
const listeners = new Set<() => void>()

function notify() {
  for (const listener of [...listeners]) listener()
}

/**
 * Marks a modal (dialog, alert dialog, sheet) as open until the returned function is called. The design system's
 * modal components do this themselves; call it only for a custom modal surface.
 */
export function registerOpenModal(): () => void {
  const token = Symbol("modal")
  openModals.add(token)
  notify()
  return () => {
    if (openModals.delete(token)) notify()
  }
}

/** Whether a modal is open right now. */
export function isModalOpen(): boolean {
  return openModals.size > 0
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Re-renders when a modal opens or closes. Use it to keep overlays and global shortcuts out of a modal's way. */
export function useModalOpen(): boolean {
  return useSyncExternalStore(subscribe, isModalOpen, () => false)
}

/** For a modal surface: counts as an open modal for as long as the calling component is mounted (or `open` is true). */
export function useModalRegistration(open = true): void {
  useEffect(() => (open ? registerOpenModal() : undefined), [open])
}
